import BaseRequest from './Requests/BaseRequest';
import { TokenManager } from './TokenManager';
import { isTeamUpLoggedIn } from './Utilities';

export default class HTTPRequest {
    /**
     * HTTP method used in request
     *
     * @param {string}
     */
    method = 'GET';

    /**
     * API base url in request
     *
     * @param {string}
     */
    baseUrl = process.env.TEAM_UP_API_BASE;

    /**
     * API url in request which should exclude the API base
     *
     * @param {string}
     */
    url = '';

    /**
     * Additional headers required in request
     *
     * @param {object}
     */
    headers = {};

    /**
     * Body params required in request
     *
     * @param {object}
     */
    body = {};

    /**
     * Query params required in request
     *
     * @param {object}
     */
    query = {};

    /**
     * Handled results from the response from the API request
     *
     * @param {array}
     */
    results = [];

    /**
     * Handled errors from the response from the API request
     *
     * @param {array}
     */
    errors = [];

    /**
     * Handled field errors from the response from the API request which will be displayed to user
     *
     * @param {array}
     */
    fieldErrors = [];

    /**
     * Total results from the response from the API request
     *
     * @param {integer}
     */
    total = 0;

    /**
     * Current page of the API request
     *
     * @param {integer}
     */
    page = 1;

    /**
     * Maxmium pages of to fetch from the API request
     *
     * @param {integer}
     */
    perPage = 20;

    /**
     * Maxmium total pages of results to fetch from the API request
     *
     * @param {integer}
     */
    totalPages = 1;

    /**
     * Whether to auth refresh has been attempted
     *
     * @param {boolean}
     */
    authRefresh = false;

    /**
     * Request constructor
     *
     * @param {string} method
     * @param {string} url
     * @param {object} headers
     * @param {object} query
     * @param {object} body
     * @param {integer} perPage
     */
    constructor(
        method = 'GET',
        url = '',
        headers = {},
        query = {},
        body = {},
        perPage = 20,
    ) {
        this.setMethod(method);
        this.setUrl(url);
        this.setHeaders(headers);
        this.setQuery(query);
        this.setBody(body);
        this.setPerPage(perPage);
    }

    /**
     * @param {string} method
     * @return {string}
     */
    setMethod(method) {
        this.method = method;
        return this.method;
    }

    /**
     * @return {string}
     */
    getMethod() {
        return this.method;
    }

    /**
     * @param {string} baseUrl
     * @return {string}
     */
    setBaseUrl(baseUrl) {
        this.baseUrl = baseUrl;
        return this.baseUrl;
    }

    /**
     * @return {string}
     */
    getBaseUrl() {
        return this.baseUrl;
    }

    /**
     * @param {string} url
     * @return {string}
     */
    setUrl(url) {
        this.url = url;
        return this.url;
    }

    /**
     * @return {string}
     */
    getUrl() {
        return this.url;
    }

    /**
     * @param {object} headers
     * @return {object}
     */
    setHeaders(headers) {
        this.headers = { ...this.headers, ...headers };
        return this.headers;
    }

    /**
     * @return {object}
     */
    getHeaders() {
        return this.headers;
    }

    /**
     * @param {object} query
     * @return {object}
     */
    setQuery(query) {
        this.query = { ...this.query, ...query };
        return this.query;
    }

    /**
     * @return {object}
     */
    getQuery() {
        return this.query;
    }

    /**
     * @param {object} body
     * @return {object}
     */
    setBody(body) {
        this.body = { ...this.body, ...body };
        return this.body;
    }

    /**
     * @return {object}
     */
    getBody() {
        return this.body;
    }

    /**
     * @param {array} results
     * @return {array}
     */
    setResults(results) {
        this.results =
            this.results.length > 0 ? this.results.concat(results) : results;

        return this.results;
    }

    /**
     * @return {array}
     */
    getResults() {
        return this.results;
    }

    /**
     * @param {array} errors
     * @return {array}
     */
    setErrors(errors) {
        this.errors =
            this.errors.length > 0 ? this.errors.concat(errors) : errors;

        return this.errors;
    }

    /**
     * @return {array}
     */
    getErrors() {
        return this.errors;
    }

    /**
     * @param {array} fieldErrors
     * @return {array}
     */
    setFieldErrors(fieldErrors) {
        this.fieldErrors =
            this.fieldErrors.length > 0
                ? this.fieldErrors.concat(fieldErrors)
                : fieldErrors;

        return this.fieldErrors;
    }

    /**
     * @return {array}
     */
    getFieldErrors() {
        return this.fieldErrors;
    }

    /**
     * @param {string} error
     * @return {void}
     */
    logError(error) {
        console.log(`${this.getBaseUrl()}${this.getUrl()}: ${error}`);
    }

    /**
     * @param {integer} total
     * @return {integer}
     */
    setTotal(total) {
        this.total = total;
        return this.total;
    }

    /**
     * @return {integer}
     */
    getTotal() {
        return this.total;
    }

    /**
     * @param {integer} page
     * @return {integer}
     */
    setPage(page) {
        this.page = page;
        return this.page;
    }

    /**
     * @return {integer}
     */
    getPage() {
        return this.page;
    }

    /**
     * @param {integer} perPage
     * @return {integer}
     */
    setPerPage(perPage) {
        this.perPage = perPage;
        return this.perPage;
    }

    /**
     * @return {integer}
     */
    getPerPage() {
        return this.perPage;
    }

    /**
     * @param {integer} totalPages
     * @return {integer}
     */
    setTotalPages(totalPages) {
        this.totalPages = totalPages;
        return this.totalPages;
    }

    /**
     * @return {integer}
     */
    getTotalPages() {
        return this.totalPages;
    }

    /**
     * @param {boolean} authRefresh
     * @return {boolean}
     */
    setAuthRefresh(authRefresh) {
        this.authRefresh = authRefresh;
        return this.authRefresh;
    }

    /**
     * @return {boolean}
     */
    getAuthRefresh() {
        return this.authRefresh;
    }

    /**
     * @return {object}
     */
    buildPayload() {
        let options = {
            method: this.getMethod(),
            headers: this.getHeaders(),
        };

        let bodyData = this.getBody();
        if (Object.keys(bodyData).length > 0) {
            if (
                'Content-Type' in this.headers &&
                this.headers['Content-Type'] == 'multipart/form-data'
            ) {
                let formData = new FormData();
                for (const key in bodyData) {
                    formData.append(key, bodyData[key]);
                }

                options.body = formData;
            } else {
                options.body = JSON.stringify(bodyData);
            }
        }

        return options;
    }

    /**
     * @return {string}
     */
    buildUrl() {
        let url = new URL(`${this.getBaseUrl()}${this.getUrl()}`);

        const queryData = this.getQuery();
        if (Object.keys(queryData).length > 0) {
            for (const key in queryData) {
                if (queryData[key] != '') {
                    url.searchParams.set(key, queryData[key]);
                }
            }
        }

        return url.toString();
    }

    /**
     * @param {object}
     * @return {array}
     */
    handleResponse(responseBody) {
        try {
            if (Object.keys(responseBody).length <= 0) {
                this.setErrors([
                    {
                        field: 'api_error',
                        message: 'No response body returned',
                    },
                ]);
                this.logError('No response body returned');
                return this.getResponse();
            }

            // Attempt to fetch the standard error message
            if (
                'message' in responseBody &&
                ('code' in responseBody || 'type' in responseBody)
            ) {
                let error = '';
                if ('code' in responseBody) {
                    error += `Code: ${responseBody.code} `;
                }

                if ('type' in responseBody) {
                    error += `Type: ${responseBody.type} `;
                }

                if ('message' in responseBody) {
                    error += `Message: ${responseBody.message}`;
                }

                this.setErrors([
                    {
                        field: responseBody.code,
                        message: responseBody.message,
                    },
                ]);
                this.logError(error);
            }

            // Errors can be returned around specific fields
            if ('field_errors' in responseBody) {
                for (const field in responseBody.field_errors) {
                    // Field values is a nested object
                    let message = '';
                    if (
                        field === 'field_values' &&
                        'value' in responseBody.field_errors[field]
                    ) {
                        message = responseBody.field_errors[field].value;
                    } else {
                        message = Array.isArray(
                            responseBody.field_errors[field],
                        )
                            ? responseBody.field_errors[field][0]
                            : responseBody.field_errors[field];
                    }

                    this.setFieldErrors([
                        {
                            field: field,
                            message: message,
                        },
                    ]);
                    this.setErrors([
                        {
                            field: field,
                            message: message,
                        },
                    ]);
                    this.logError(`${field}: ${message}`);
                }
            }

            // Errors maybe returned in this format if request is forwarded
            if ('errors' in responseBody) {
                this.setErrors(responseBody.errors);
            }

            if ('fieldErrors' in responseBody) {
                this.setFieldErrors(responseBody.fieldErrors);
            }

            // Possible multiple results are returned or singular object
            if (this.errors.length <= 0) {
                let results = [responseBody];
                if ('results' in responseBody) {
                    results = responseBody.results;
                }

                this.setResults(results);

                if ('pagination' in responseBody) {
                    this.setPage(responseBody.pagination.page);
                    this.setPerPage(responseBody.pagination.perPage);
                    this.setTotal(responseBody.pagination.total);
                    this.setTotalPages(responseBody.pagination.totalPages);
                } else {
                    const total =
                        'count' in responseBody ? responseBody.count : 1;
                    const totalPages =
                        total > 1
                            ? Math.ceil(total / this.getPerPage())
                            : total;
                    this.setTotal(total);
                    this.setTotalPages(totalPages);
                }
            }
        } catch (error) {
            this.setErrors([
                {
                    field: 'api_error',
                    message: error,
                },
            ]);
            this.logError(error);
        }

        return this.getResponse();
    }

    /**
     * @return {object}
     */
    getResponse() {
        return {
            results: this.getResults(),
            pagination: {
                page: this.getPage(),
                perPage: this.getPerPage(),
                total: this.getTotal(),
                totalPages: this.getTotalPages(),
            },
            errors: this.getErrors(),
            fieldErrors: this.getFieldErrors(),
        };
    }

    /**
     * @return {object}
     */
    async sendRequest() {
        let renderError = false;

        try {
            let response = await fetch(this.buildUrl(), this.buildPayload());
            const requestHeaders = this.getHeaders();

            const baseRequest = new BaseRequest(
                requestHeaders['Teamup-Request-Mode'],
                requestHeaders['Teamup-Provider-ID'],
                requestHeaders['Teamup-Customer-ID'],
                true,
            );

            if (
                isTeamUpLoggedIn() &&
                ((response.status === 401 && !this.getAuthRefresh()) ||
                    baseRequest.isTokenExpired())
            ) {
                this.setAuthRefresh(true);

                try {
                    const newAccessToken =
                        await TokenManager.refreshTokenIfNeeded(BaseRequest);
                    const updatedPayload = this.buildPayload();
                    updatedPayload.headers.Authorization = `Token ${newAccessToken}`;

                    // Resend the original request with the new token
                    response = await fetch(this.buildUrl(), updatedPayload);
                } catch (refreshError) {
                    // If the refresh token fails, log the user out
                    // and clear the tokens
                    this.logError(
                        'Token refresh failed or refresh token expired',
                    );
                    return baseRequest.forceLogout();
                }

                // Check if the response is a customer invalid error, if so
                // customer account has likely be closed/deleted and they need
                // to be logged out
            } else if (
                response.status === 403 &&
                response?.code === 'customer_invalid' &&
                isTeamUpLoggedIn()
            ) {
                // Output error if request fails, but ensure error only renders once if processing
                // a retry request via refresh token refresh
                return baseRequest.forceLogout();
            }

            response = await response.json();
            response = this.handleResponse(response);
        } catch (error) {
            if (renderError) {
                this.logError(error);
            }
        }

        return this.getResponse();
    }

    /**
     * @param {integer} page
     * @param {boolean} fetchAll
     * @return {object}
     */
    async sendRequestPaginated(page = 1, fetchAll = false) {
        this.setPage(page);
        this.setQuery({
            page: this.getPage(),
            page_size: this.getPerPage(),
        });
        await this.sendRequest();

        // No need to fetch any more pages
        if (fetchAll && this.getTotalPages() > 1) {
            do {
                this.page++;
                this.setQuery({
                    page: this.getPage(),
                });
                await this.sendRequest();
            } while (this.getPage() < this.getTotalPages());
        }

        return this.getResponse();
    }
}
